import { useCallback, useEffect, useState, type ReactNode } from 'react'
import type { Chapter, TextArtifact, TextKind, TextKindState, TextVersionSummary } from '../api/types'
import { useEpisodeCosts } from '../hooks/useBilling'
import { useGenerateText, useSaveText, useText, useTextVersion } from '../hooks/useText'
import { Cost } from './Cost'
import { Button, Card, CopyButton, ErrorNote, LoadingBlock } from './ui'
import { parseClock, withTimestamps } from './Timestamps'

type FieldType = 'line' | 'text' | 'list' | 'tags' | 'chapters'

interface FieldSpec {
  key: string
  label: string
  type: FieldType
}

interface KindSpec {
  kind: TextKind
  label: string
  /** Reads well in a sentence: "the episode pack". */
  noun: string
  fields: FieldSpec[]
}

const EPISODE_PACK: KindSpec = {
  kind: 'episode_pack',
  label: 'Episode pack',
  noun: 'episode pack',
  fields: [
    { key: 'titles', label: 'Title options', type: 'list' },
    { key: 'summary', label: 'Summary', type: 'text' },
    { key: 'show_notes', label: 'Show notes', type: 'text' },
    { key: 'chapters', label: 'Chapters', type: 'chapters' },
    { key: 'keywords', label: 'Keywords', type: 'tags' },
  ],
}

const KINDS: KindSpec[] = [
  EPISODE_PACK,
  {
    kind: 'social',
    label: 'Social posts',
    noun: 'social posts',
    fields: [
      { key: 'x', label: 'X', type: 'text' },
      { key: 'linkedin', label: 'LinkedIn', type: 'text' },
      { key: 'instagram', label: 'Instagram', type: 'text' },
      { key: 'tiktok', label: 'TikTok', type: 'text' },
      { key: 'youtube_description', label: 'YouTube description', type: 'text' },
    ],
  },
  {
    kind: 'newsletter',
    label: 'Newsletter',
    noun: 'newsletter',
    fields: [
      { key: 'subject', label: 'Subject', type: 'line' },
      { key: 'preview_text', label: 'Preview text', type: 'line' },
      { key: 'body_markdown', label: 'Body (Markdown)', type: 'text' },
    ],
  },
  {
    kind: 'blog',
    label: 'Blog post',
    noun: 'blog post',
    fields: [
      { key: 'title', label: 'Title', type: 'line' },
      { key: 'meta_description', label: 'Meta description', type: 'line' },
      { key: 'body_markdown', label: 'Article (Markdown)', type: 'text' },
    ],
  },
]

/** `m:ss`, or `h:mm:ss` from an hour on. */
function clock(totalSeconds: number): string {
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = String(totalSeconds % 60).padStart(2, '0')
  return hours > 0 ? `${hours}:${String(minutes).padStart(2, '0')}:${seconds}` : `${minutes}:${seconds}`
}

/** Seconds from `m:ss` or `h:mm:ss`, or null. */

function serialize(type: FieldType, value: unknown): string {
  if (type === 'chapters') return ((value as Chapter[] | undefined) ?? []).map((c) => `${clock(c.start_seconds)} ${c.title}`).join('\n')
  if (type === 'list' || type === 'tags') return ((value as string[] | undefined) ?? []).join('\n')
  return typeof value === 'string' ? value : ''
}

/** The edited text back into the field's shape; throws with a reason. */
function deserialize(type: FieldType, text: string): unknown {
  const lines = text.split('\n').map((line) => line.trim()).filter(Boolean)
  if (type === 'list' || type === 'tags') return lines
  if (type === 'chapters') {
    return lines.map((line, index) => {
      const match = /^(\d{1,2}(?::\d{1,2}){1,2})\s+(.+)$/.exec(line)
      const seconds = match ? parseClock(match[1] ?? '') : null
      if (!match || seconds == null) throw new Error(`Line ${index + 1}: start with a time like 12:34, then the title.`)
      return { start_seconds: seconds, title: match[2] ?? '' }
    })
  }
  return text.trim()
}

function describe(artifact: TextArtifact): string {
  const who = artifact.source === 'edit' ? 'Edited by hand' : `Written by ${artifact.model ?? 'the model'}`
  const when = new Date(artifact.created_at).toLocaleString()
  return artifact.instruction ? `${who} · “${artifact.instruction}” · ${when}` : `${who} · ${when}`
}

/** Titles, show notes, chapters, posts and articles written from the transcript. */
export function TextPanel({
  workspaceId,
  episodeId,
  transcriptReady,
  canEdit,
  onSeek,
}: {
  workspaceId: string
  episodeId: string
  transcriptReady: boolean
  canEdit: boolean
  onSeek: (ms: number) => void
}) {
  const text = useText(workspaceId, episodeId, transcriptReady)
  const [selected, setSelected] = useState<TextKind>('episode_pack')
  const spec = KINDS.find((k) => k.kind === selected) ?? EPISODE_PACK
  const state = text.data?.kinds.find((k) => k.kind === selected)
  return (
    <Card title="Text">
      <div className="stack">
        <div className="tp-tabs" role="tablist" aria-label="Kinds of text">
          {KINDS.map((k) => {
            const status = text.data?.kinds.find((s) => s.kind === k.kind)?.status
            return (
              <button
                key={k.kind}
                type="button"
                role="tab"
                aria-selected={k.kind === selected}
                className={`tp-tab${k.kind === selected ? ' is-active' : ''}`}
                onClick={() => setSelected(k.kind)}
              >
                {k.label}
                {status && status !== 'none' && <span className={`tp-dot tp-dot-${status}`} title={status} />}
              </button>
            )
          })}
        </div>
        {!transcriptReady ? (
          <p className="muted">Titles, show notes, posts and articles are written from the transcript. Ask for one on the Transcript card first.</p>
        ) : state ? (
          <KindPanel key={selected} workspaceId={workspaceId} episodeId={episodeId} spec={spec} state={state} canEdit={canEdit} onSeek={onSeek} />
        ) : text.isError ? (
          <ErrorNote error={text.error} />
        ) : (
          <LoadingBlock />
        )}
      </div>
    </Card>
  )
}

function KindPanel({
  workspaceId,
  episodeId,
  spec,
  state,
  canEdit,
  onSeek,
}: {
  workspaceId: string
  episodeId: string
  spec: KindSpec
  state: TextKindState
  canEdit: boolean
  onSeek: (ms: number) => void
}) {
  const generate = useGenerateText(workspaceId, episodeId)
  const save = useSaveText(workspaceId, episodeId)
  const cost = useEpisodeCosts(workspaceId, episodeId).data?.text.find((t) => t.kind === spec.kind)?.micro_credits
  const [viewing, setViewing] = useState<number | null>(null)
  const [instruction, setInstruction] = useState('')
  const older = useTextVersion(workspaceId, episodeId, spec.kind, viewing)
  const latest = state.latest
  const shown = viewing == null ? latest : older.data
  const busy = state.status === 'pending' || generate.isPending
  const run = (revise: boolean) =>
    generate.mutate(
      { kind: spec.kind, instruction: revise ? instruction.trim() : undefined, reviseVersion: revise && latest ? latest.version : undefined },
      { onSuccess: () => setInstruction('') },
    )
  const { mutateAsync } = save
  const saveField = useCallback(
    async (key: string, next: unknown) => {
      if (!latest) return
      await mutateAsync({ kind: spec.kind, content: { ...latest.content, [key]: next }, baseVersion: latest.version })
    },
    [mutateAsync, latest, spec.kind],
  )
  return (
    <div className="stack">
      {generate.error && <ErrorNote error={generate.error} />}
      {state.status === 'pending' && (
        <p className="muted" data-testid="text-pending">
          Writing the {spec.noun}… This usually takes under a minute.
        </p>
      )}
      {state.status === 'failed' && <ErrorNote message={`Writing the ${spec.noun} failed: ${state.error ?? 'no reason was given.'}`} />}
      {!latest &&
        state.status !== 'pending' &&
        (canEdit ? (
          <div>
            <Button busy={generate.isPending} onClick={() => run(false)}>
              Write the {spec.noun} <Cost micro={cost} />
            </Button>
          </div>
        ) : (
          <p className="muted">Nothing written yet.</p>
        ))}
      {latest && (
        <>
          <div className="row between">
            <VersionPicker versions={state.versions} value={viewing ?? latest.version} onChange={(v) => setViewing(v === latest.version ? null : v)} />
            {shown && <span className="muted small">{describe(shown)}</span>}
          </div>
          {viewing != null && (
            <div className="notice small row between">
              <span>You are looking at an older version.</span>
              <button type="button" className="tx-link" onClick={() => setViewing(null)}>
                Back to the latest
              </button>
            </div>
          )}
          {shown ? (
            <div className="tp-fields" data-testid={`text-${spec.kind}`}>
              {spec.fields.map((field) => (
                <FieldView
                  key={`${shown.version}-${field.key}`}
                  field={field}
                  value={shown.content[field.key]}
                  editable={canEdit && viewing == null}
                  saving={save.isPending}
                  onSeek={onSeek}
                  onSave={(next) => saveField(field.key, next)}
                />
              ))}
            </div>
          ) : older.isError ? (
            <ErrorNote error={older.error} />
          ) : (
            <LoadingBlock />
          )}
          {save.error && <ErrorNote error={save.error} />}
          {canEdit && viewing == null && (
            <form
              className="row tp-regenerate"
              onSubmit={(event) => {
                event.preventDefault()
                if (instruction.trim()) run(true)
              }}
            >
              <input
                value={instruction}
                onChange={(event) => setInstruction(event.target.value)}
                placeholder="Rewrite with an instruction, e.g. shorter titles, no puns"
                maxLength={1000}
                aria-label="Instruction"
                disabled={busy}
              />
              <Button type="submit" variant="secondary" busy={generate.isPending} disabled={busy || !instruction.trim()}>
                Rewrite <Cost micro={cost} />
              </Button>
              <Button variant="ghost" disabled={busy} onClick={() => run(false)}>
                Write a fresh one <Cost micro={cost} />
              </Button>
            </form>
          )}
        </>
      )}
    </div>
  )
}

function VersionPicker({ versions, value, onChange }: { versions: TextVersionSummary[]; value: number; onChange: (version: number) => void }) {
  if (versions.length < 2) return <span className="muted small">Version {value}</span>
  return (
    <select className="tp-version" value={value} onChange={(event) => onChange(Number(event.target.value))} aria-label="Version">
      {versions.map((v) => (
        <option key={v.version} value={v.version}>
          Version {v.version}
          {v.source === 'edit' ? ' · edited' : ''}
          {v.instruction ? ` · “${v.instruction.length > 40 ? `${v.instruction.slice(0, 40)}…` : v.instruction}”` : ''}
        </option>
      ))}
    </select>
  )
}


function FieldView({
  field,
  value,
  editable,
  saving,
  onSeek,
  onSave,
}: {
  field: FieldSpec
  value: unknown
  editable: boolean
  saving: boolean
  onSeek: (ms: number) => void
  onSave: (next: unknown) => Promise<void>
}) {
  const [draft, setDraft] = useState<string | null>(null)
  const [problem, setProblem] = useState<string | null>(null)
  const text = serialize(field.type, value)
  const submit = (draftText: string) => {
    let next: unknown
    try {
      next = deserialize(field.type, draftText)
    } catch (error) {
      setProblem(error instanceof Error ? error.message : String(error))
      return
    }
    setProblem(null)
    // A failed save shows on the panel; the editor stays open.
    onSave(next).then(() => setDraft(null), () => undefined)
  }
  return (
    <div className="tp-field" data-field={field.key}>
      <div className="tp-field-head">
        <span className="tp-field-label">{field.label}</span>
        {draft == null && (
          <span className="tp-field-actions">
            <CopyButton text={text} />
            {editable && (
              <button type="button" className="tx-link small" onClick={() => setDraft(text)}>
                Edit
              </button>
            )}
          </span>
        )}
      </div>
      {draft != null ? (
        <form
          className="tp-editor stack-sm"
          onSubmit={(event) => {
            event.preventDefault()
            submit(draft)
          }}
        >
          <textarea
            rows={field.type === 'line' ? 2 : Math.min(14, Math.max(3, draft.split('\n').length + 1))}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            aria-label={field.label}
            autoFocus
          />
          {field.type === 'chapters' && <span className="muted small">One chapter per line: a time like 12:34, then the title.</span>}
          {(field.type === 'list' || field.type === 'tags') && <span className="muted small">One per line.</span>}
          {problem && <span className="small error-text">{problem}</span>}
          <div className="row">
            <Button type="submit" busy={saving}>
              Save
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setDraft(null)
                setProblem(null)
              }}
            >
              Cancel
            </Button>
          </div>
        </form>
      ) : (
        <FieldValue field={field} value={value} onSeek={onSeek} />
      )}
    </div>
  )
}

function FieldValue({ field, value, onSeek }: { field: FieldSpec; value: unknown; onSeek: (ms: number) => void }) {
  switch (field.type) {
    case 'list': {
      const items = (value as string[] | undefined) ?? []
      return items.length ? (
        <ol className="tp-list">
          {items.map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ol>
      ) : (
        <span className="muted">None</span>
      )
    }
    case 'tags': {
      const items = (value as string[] | undefined) ?? []
      return items.length ? (
        <div className="tp-chips">
          {items.map((item) => (
            <span key={item} className="tp-chip">
              {item}
            </span>
          ))}
        </div>
      ) : (
        <span className="muted">None</span>
      )
    }
    case 'chapters': {
      const chapters = (value as Chapter[] | undefined) ?? []
      return chapters.length ? (
        <ol className="tp-chapters">
          {chapters.map((chapter) => (
            <li key={chapter.start_seconds}>
              <button type="button" className="tx-link" onClick={() => onSeek(chapter.start_seconds * 1000)} title="Play from here">
                {clock(chapter.start_seconds)}
              </button>
              <span>{chapter.title}</span>
            </li>
          ))}
        </ol>
      ) : (
        <span className="muted">None</span>
      )
    }
    default: {
      const text = typeof value === 'string' ? value : ''
      return text ? <p className="tp-text">{withTimestamps(text, onSeek)}</p> : <span className="muted">Empty</span>
    }
  }
}
